import React, {Component} from 'react'
import propTypes from 'prop-types'
import {get, has, isNil, isEmpty} from 'lodash'
import iconNames from 'theme/icons'
import {prefix} from 'helpers/component'
import {SelectField} from 'components/selectField'
import Button from 'components/button'
import DatePicker from 'components/datePicker'
import NumberField from 'components/numberField'
import TextField from 'components/textField'
import TimePicker from 'components/timePicker'
import {formatValue} from 'containers/editablePivotTable'
import CompanySelectField from 'components/companySelectField'
import Checkbox from '@material-ui/core/Checkbox'
import {getColumnOptions} from 'helpers/pivotTable'
import {CellDataType} from './cellDataType'

const getClassName = prefix('editablePivotTable-cellEditor-')

// Key codes for keys which input a character
const charKeyCodes = [
  48, 49, 50, 51, 52, 53, 54, 55, 56, 57, 59, 61, 65, 66, 67, 68, 69, 70, 71, 72, 73, 74, 75, 76, 77, 78, 79, 80,
  81, 82, 83, 84, 85, 86, 87, 88, 89, 90, 96, 97, 98, 99, 100, 101, 102, 103, 104, 105, 106, 107, 108, 109, 110,
  111, 186, 187, 188, 189, 190, 191, 192, 219, 220, 221, 222,
]
const listTypes = ['List', 'Multi List', 'Lookup', 'Boolean', 'Company List']

const styles = {
  input: {
    position: 'absolute',
    top: 0,
    left: 0,
    bottom: 0,
    width: 'calc(100% - 10px)',
    padding: 5,
    border: 'none',
    background: 'transparent',
  },
  muiInput: {
    marginTop: -18,
    marginBottom: -16,
    marginLeft: -5,
    height: 'auto',
    width: '100%',
    padding: 5,
    fontSize: 13,
    lineHeight: '18px',
  },
  muiInputDateTime_Date: {
    width: 'calc(100% - 55px)',
  },
  muiInputDateTime_Time: {
    width: 45,
    left: 'calc(100% - 45px)',
  },
  muiHint: {
    top: 5,
    bottom: 'auto',
    width: 'calc(100% - 10px)',
    textAlign: 'left',
    fontSize: 13,
  },
  muiMemoHint: {
    top: 16,
    bottom: 'auto',
    width: 'calc(100% - 10px)',
    textAlign: 'left',
    fontSize: 13,
  },
  numberHint: {
    position: 'absolute',
    top: 12,
    width: 'calc(100% - 5px)',
    textAlign: 'left',
    fontSize: 13,
  },
}

class CellEditor extends Component {
  constructor(props) {
    super(props)
    this.state = {
      editMode: false,
    }
  }

  setDatePart = (datetime, date) => {
    const ret = new Date(datetime.getTime())
    ret.setFullYear(date.getFullYear())
    ret.setMonth(date.getMonth())
    ret.setDate(date.getDate())
    return ret
  }

  setTimePart = (datetime, time) => {
    const ret = new Date(datetime.getTime())
    ret.setHours(time.getHours())
    ret.setMinutes(time.getMinutes())
    return ret
  }

  change = (value, meta) => {
    const {column, entity} = this.props
    this.props.onChange(column, entity.id, entity, value, meta, true, isNew => {
      if (isNew) {
        this.props.onAdd(false, true)
        // We'll add a delay to allow re-render
        window.setTimeout(() =>
          this.moveCell(0, 1, true, false), 100)
      }
    })
  }

  getListEditor = (
    entity, column, key, value, multi, values, async = false, loadOptions, defaultOptions, fieldProps
  ) => {
    return (
      <SelectField
        ref={e => {
          this.editor = e
        }}
        placeholder={column.label}
        value={value}
        options={values}
        multi={multi}
        openOnFocus
        clearable={column.required !== true}
        async={async}
        loadOptions={loadOptions}
        defaultOptions={defaultOptions}
        OptionRenderer={column.optionRenderer}
        styles={{
          control: (provided) => {
            return {
              ...provided,
              height: 'auto',
              minHeight: '100%',
            }
          },
        }}
        onClick={e => e.stopPropagation()}
        onChange={value => {
          if (multi) {
            this.change(value ? value.map(v => v.value) : null, value)
          } else {
            this.change(value ? value.value : null, value)
            this.cancelEdit(true)
          }
        }}
        onInputKeyDown={e => {
          if (e.keyCode === 27) this.cancelEdit(true)
        }}
        onBlur={() => this.cancelEdit(false)}
        {...fieldProps}
      />
    )
  }

  loadLookupItems = (column, entity) => (input, callback) => {
    if (column.getItems) {
      Promise.resolve(column.getItems(input, entity)).then(options => {
        options = options || []
        if (input && input.length > 0 && column.allowNewItems(entity)) options.push({value: input, label: input})
        callback(options)
      })
    } else {
      callback([{value: -1, label: 'WARNING: Column does not support getItems'}])
    }
  }

  getDataType = () => {
    const {column, entity} = this.props
    if (column.dataTypeForEntity) return column.dataTypeForEntity(entity)
    return column.dataType || 'String'
  }

  getEditor = (entity, key, value) => {
    const {column} = this.props
    const dataType = this.getDataType()

    const align = column.align || 'left'
    let style = {textAlign: align}
    if (column.style) style = {...style, ...column.style(entity)}
    switch (dataType) {

      case CellDataType.STRING:
      case CellDataType.MEMO:
      case CellDataType.PHONE:
      case CellDataType.MOBILE:
      case CellDataType.EMAIL:

        let type = 'text', hintStyle = styles.muiHint
        switch (dataType) {
          case CellDataType.PHONE:
          case CellDataType.MOBILE:
            type = 'phone'
            break
          case CellDataType.EMAIL:
            type = 'email'
            break
          case CellDataType.MEMO:
            hintStyle = styles.muiMemoHint
            break
        }

        if (value === null) value = ''
        return (
          <TextField
            type={type}
            ref={e => {
              this.editor = e
            }}
            hintText={column.label}
            onClick={e => e.stopPropagation()}
            onChange={this.change}
            onBlur={() => this.cancelEdit(false)}
            style={styles.muiInput}
            hintStyle={{...hintStyle, textAlign: align}}
            inputStyle={style}
            value={value || ''}
            multiLine={dataType === 'Memo'}
            underlineShow={false}
          />
        )

      case CellDataType.NUMBER:
        const columnOptions = getColumnOptions(entity, column)
        return (
          <NumberField
            ref={e => {
              this.editor = e
            }}
            hintText={column.label}
            options={columnOptions}
            showStepper={has(columnOptions, 'step') && (columnOptions?.showStepper ?? true)}
            onClick={e => e.stopPropagation()}
            onBlur={() => this.cancelEdit(false)}
            onChange={this.change}
            hintStyle={{...styles.numberHint, textAlign: align}}
            inputStyle={style}
            value={value || ''}
          />
        )

      case CellDataType.MULTI_LIST:
        return this.getListEditor(
          entity,
          column,
          key,
          value,
          true,
          column.values,
          false,
          null,
          null,
          column.selectFieldProps
        )

      case CellDataType.LIST:
        return this.getListEditor(
          entity,
          column,
          key,
          value,
          false,
          column.values,
          false,
          null,
          null,
          column.selectFieldProps
        )

      case CellDataType.BOOLEAN:
        return this.getListEditor(
          entity,
          column,
          key,
          value,
          false,
          [
            {
              id: true,
              key: true,
              value: true,
              label: 'Yes',
            },
            {
              id: false,
              key: false,
              value: false,
              label: 'No',
            },
          ],
          false,
          null,
          null,
          column.selectFieldProps
        )

      case CellDataType.CHECKBOX:
        return (
          <Checkbox
            checked={value}
            disabled={!this.canEdit()}
            onChange={(e, isChecked) => this.change(isChecked)}
            style={{width: 24, height: 24}}/>
        )

      case CellDataType.LOOKUP:
        return this.getListEditor(
          entity,
          column,
          key,
          value,
          false,
          null,
          true,
          this.loadLookupItems(column, entity),
          column.showInitialItems,
          {
            searchPromptText: column.searchPromptText,
            ...column.selectFieldProps,
          }
        )

      case CellDataType.DATE:
        return (<DatePicker
          value={value}
          ref={e => {
            this.editor = e ? e.getWrappedInstance() : null
          }}
          hintText={column.label}
          hintStyle={styles.muiHint}
          textFieldStyle={styles.muiInput}
          onClick={e => e.stopPropagation()}
          onClose={() => this.cancelEdit(false)}
          onChange={(e, v) => this.change(v)}
        />)

      case CellDataType.TIME:
        return (<TimePicker
          value={value}
          ref={e => {
            this.editor = e
          }}
          hintText={column.label}
          hintStyle={styles.muiHint}
          textFieldStyle={styles.muiInput}
          onClick={e => e.stopPropagation()}
          onChange={(e, v) => this.change(v)}
        />)

      case CellDataType.DATE_TIME:
        return (<div>
          <DatePicker
            value={value}
            ref={e => {
              this.editor = e ? e.getWrappedInstance() : null
            }}
            hintText={column.label}
            hintStyle={styles.muiHint}
            textFieldStyle={{...styles.muiInput, ...styles.muiInputDateTime_Date}}
            onClick={e => e.stopPropagation()}
            onClose={() => this.cancelEdit(false)}
            onChange={(e, v) => this.change(this.setDatePart(value, v))}
          />
          <TimePicker
            value={value}
            textFieldStyle={{...styles.muiInput, ...styles.muiInputDateTime_Time}}
            onClick={e => e.stopPropagation()}
            onChange={(e, v) => onChange(this.setTimePart(value, v))}
          />
        </div>)

      case CellDataType.COMPANY_LIST:
        return (
          <CompanySelectField
            ref={e => this.input = e}
            onChange={(v) => {
              this.change(v)
              this.cancelEdit(true)
            }}
            value={value}
            onClick={e => e.stopPropagation()}
            onInputKeyDown={e => {
              if (e.keyCode === 27) this.cancelEdit(true)
            }}
            onBlur={() => this.cancelEdit(false)}
            autofocus
          />
        )

      default:
        return <div>Data Type Not Supported</div>
    }
  }

  formatValue = (entity, value) => {
    const {column, locale} = this.props
    return formatValue(entity, column, value, locale)
  }

  keyDown = (e) => {
    const {onSelectRelativeRange, onKeyDown} = this.props
    const editMode = this.isEditMode()

    if (editMode) {
      switch (e.keyCode) {
        case 9:   // TAB
          this.moveCell(e.shiftKey ? -1 : 1, 0, true)
          e.preventDefault()
          break
        case 13:  // ENTER
          if (this.getDataType() !== 'Memo' || e.shiftKey) {
            this.cancelEdit(false)
            this.moveCell(0, e.shiftKey ? 0 : 1)
          }
          break
        case 27:  // ESC
          this.cancelEdit(true)
          break
      }
    } else {
      if (onKeyDown(e) === false) {
        e.preventDefault()
        return false
      }

      switch (e.keyCode) {
        case 8:   // BACKSPACE
          this.change(null)
          e.preventDefault()
          break
        case 9:   // TAB
          this.moveCell(e.shiftKey ? -1 : 1, 0, true)
          e.preventDefault()
          break
        case 13:  // ENTER
          this.edit() // note: different behaviour to standard spreadsheet
          // this.moveCell(0, 1)
          e.preventDefault()
          break
        case 37:  // left
          e.shiftKey ? onSelectRelativeRange(-1, 0) : this.moveCell(-1, 0)
          e.preventDefault()
          break
        case 38:  // up
          e.shiftKey ? onSelectRelativeRange(0, -1) : this.moveCell(0, -1)
          e.preventDefault()
          break
        case 39:  // right
          e.shiftKey ? onSelectRelativeRange(1, 0) : this.moveCell(1, 0)
          e.preventDefault()
          break
        case 40:  // down
          e.shiftKey ? onSelectRelativeRange(0, 1) : this.moveCell(0, 1)
          e.preventDefault()
          break
        case 113: // F2
          this.edit()
          e.preventDefault()
          break
        case 127:   // DEL
          this.change(null)
          e.preventDefault()
          break

        default:
          if (!e.ctrlKey && !e.metaKey && charKeyCodes.indexOf(e.keyCode) >= 0) {
            this.edit(true)
            break
          }
      }
    }
  }

  moveCell = (x, y, wrap = false, edit = false) => {
    const {onMoveCell, rowIndex, colIndex} = this.props
    onMoveCell(colIndex, rowIndex, x, y, wrap, edit)
  }

  canEdit = () => {
    const {entity, column, onCanEdit} = this.props
    let ret = column.editable
    if (ret && onCanEdit) ret = onCanEdit(entity, column)
    return ret
  }

  edit = (clear) => {
    if (this.canEdit()) {
      this.setState({editMode: true}, () => {
        if (this.editor) {
          if (this.editor.focus) this.editor.focus(true)
          if (clear && this.editor.clear) this.editor.clear()
        }
      })
    }
  }

  cancelEdit = (stealFocus) => {
    this.setState({editMode: false}, () => {
      if (stealFocus) this.container.focus()
    })
  }

  doubleClick = () => {
    if (!this.state.editMode) this.edit()
  }

  focus = () => {
    this.container.focus()
  }

  onFocus = () => {
    const {rowIndex, colIndex, onSelect} = this.props
    onSelect(colIndex, rowIndex, false, false, false)
  }

  onMouseDown = (e) => {
    const {colIndex, rowIndex, onSelect, onClearRange, onMouseDown} = this.props

    const range = e.shiftKey
    const append = (e.ctrlKey || e.metaKey)

    // No focus when range selecting
    if (range || append) {
      onSelect(colIndex, rowIndex, range, append, true)
      e.preventDefault()
      e.stopPropagation()
    } else {
      onClearRange()
    }

    onMouseDown(e, colIndex, rowIndex)
  }

  onMouseMove = (e) => {
    const {colIndex, rowIndex, onMouseMove} = this.props
    onMouseMove(e, colIndex, rowIndex)
  }

  /**
   * @bool hideFixedRowAddButton - Set to default false in propDefaults.
   * In scenarios where we want the fixed bottom row to render but without the addButton.
   * @bool hideFixedRowAddButton is set in ColumnDefinitions
   * - see helper > priceBreaks > getColumnDefinition.js, used in partPackPriceBreakPivotTable.js component
   */
  isAddButtonVisible = (hideFixedRowAddButton, isFixed, colIndex) => {
    const {adding, hideFixedRowAddButtonWhilstAdding} = this.props

    return !hideFixedRowAddButton &&
      isFixed &&
      colIndex === 0 &&
      (!adding || !hideFixedRowAddButtonWhilstAdding)
  }

  isEditMode = () => {
    const {column} = this.props
    const {editMode} = this.state
    return window.isTouch || column.editMode || editMode
  }

  render() {
    const {
      entity, column, rowIndex, colIndex, level, isFixed, containerClassName, contentClassName,
      getSelected, onAdd, hideFixedRowAddButton, adding, fixedRowAddButtonLabel,
    } = this.props
    let {containerStyle, contentStyle} = this.props

    const editMode = this.isEditMode()
    const key = entity.id // ASSUMED ID

    let value = null
    const isEditing = editMode && this.canEdit()
    if (column.data) value = column.data(entity, isEditing)

    const edited = get(entity, ['_editedKeys', column.key], false)
    const selected = getSelected ? getSelected(colIndex, rowIndex, level) : {}
    const dataType = this.getDataType()
    const isCheckbox = dataType === 'Checkbox'

    let content
    if (isEditing) {
      content = this.getEditor(entity, key, value)
    } else {
      if (entity.isNewRow && (isNil(value) || value === '')) {
        if (adding) content = <div className={getClassName('placeholder')}>{column.label}</div>
      } else {
        content = isCheckbox
          ? this.getEditor(entity, key, value)
          : this.formatValue(entity, value)
      }
    }

    const classes = ['container', `cell-${column.key}`]
    if (this.canEdit()) classes.push('container-editable')
    if (this.state.editMode && !isCheckbox) classes.push('container-editMode')
    if (edited) classes.push('container-edited')
    if (selected.selected) classes.push('container-selected')
    if (selected.rangeRight) classes.push('container-range-right')
    if (selected.rangeLeft) classes.push('container-range-left')
    if (selected.rangeDown) classes.push('container-range-down')
    if (selected.rangeUp) classes.push('container-range-up')
    if (isEmpty(value)) classes.push('container-empty')

    if (editMode && listTypes.indexOf(dataType) >= 0) {
      containerStyle = {...containerStyle, overflow: 'visible'}
      contentStyle = {...contentStyle, overflow: 'visible'}
    }

    return (
      <div
        style={containerStyle}
        className={containerClassName}
        onMouseDown={this.onMouseDown}
        onMouseMove={this.onMouseMove}
      >
        <div
          style={contentStyle}
          className={`${getClassName(classes)} ${contentClassName}`}
          tabIndex={1}
          onKeyDown={this.keyDown}
          onDoubleClick={this.doubleClick}
          onFocus={this.onFocus}
          ref={e => {
            this.container = e
          }}
        >
          {content}
        </div>
        {this.isAddButtonVisible(hideFixedRowAddButton, isFixed, colIndex)
          ? (
            <Button
              className={getClassName('addButton')}
              label={fixedRowAddButtonLabel}
              tooltip="Add a new row"
              icon={iconNames.add}
              flat={true}
              onClick={() => onAdd(true, !this.props.editBeforePersist)}
            />
          )
          : null
        }
      </div>
    )
  }
}

CellEditor.defaultProps = {
  hideFixedRowAddButton: false,
  fixedRowAddButtonLabel: 'Quick Add',
  hideFixedRowAddButtonWhilstAdding: false,
  adding: false,
}

CellEditor.propTypes = {
  entity: propTypes.object.isRequired,
  column: propTypes.object.isRequired,
  rowIndex: propTypes.number.isRequired,
  colIndex: propTypes.number.isRequired,
  level: propTypes.number.isRequired,
  adding: propTypes.bool,
  isFixed: propTypes.bool,
  containerStyle: propTypes.object,
  containerClassName: propTypes.string,
  contentStyle: propTypes.object,
  contentClassName: propTypes.string,
  className: propTypes.string,
  getSelected: propTypes.func,
  locale: propTypes.string,
  hideFixedRowAddButton: propTypes.bool,
  fixedRowAddButtonLabel: propTypes.string,
  hideFixedRowAddButtonWhilstAdding: propTypes.bool,
  onChange: propTypes.func.isRequired,
  onSelect: propTypes.func.isRequired,
  onAdd: propTypes.func,
  onCanEdit: propTypes.func,
  onMoveCell: propTypes.func.isRequired,
  onSelectRange: propTypes.func.isRequired,
  onSelectRelativeRange: propTypes.func.isRequired,
  onClearRange: propTypes.func.isRequired,
  onMouseDown: propTypes.func.isRequired,
  onMouseMove: propTypes.func.isRequired,
  onKeyDown: propTypes.func.isRequired,
  editBeforePersist: propTypes.bool.isRequired,
}

export default CellEditor
